import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../App';
import collegeConfig from '../../config/collegeConfig';
import { generateClassicDRPCertificate } from '../../utils/classicDrpCertificateGenerator';
import {
  FaUserMd, FaUsers, FaCheckCircle, FaTimesCircle, FaCalendarAlt,
  FaHospital, FaBook, FaClipboardList, FaHeartbeat, FaSignOutAlt,
  FaArrowLeft, FaSpinner, FaDownload, FaAward, FaSave, FaInfoCircle
} from 'react-icons/fa';

const API = collegeConfig.api.baseURL;

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const CATEGORIES = ['Observed', 'Assisted', 'Performed under supervision', 'Performed Independently'];

// ─── Verification section ────────────────────────────────────────────────────
const VerifySection = ({ label, verified, onToggle, children, count }) => (
  <div style={{ marginBottom: 16, border: `2px solid ${verified ? '#a7f3d0' : '#fca5a5'}`, borderRadius: 12, overflow: 'hidden' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: verified ? '#d1fae5' : '#fee2e2' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {verified ? <FaCheckCircle style={{ color: '#059669' }} /> : <FaTimesCircle style={{ color: '#dc2626' }} />}
        <span style={{ fontWeight: 600, fontSize: 14 }}>{label}</span>
        <span style={{ fontSize: 12, color: '#666' }}>({count} records)</span>
      </div>
      <button
        onClick={onToggle}
        style={{
          background: verified ? '#dc2626' : '#059669',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          padding: '5px 14px',
          cursor: 'pointer',
          fontSize: 12,
          fontWeight: 600
        }}
      >
        {verified ? 'Unverify' : 'Mark Verified'}
      </button>
    </div>
    <div style={{ padding: '10px 14px', maxHeight: 200, overflowY: 'auto' }}>
      {children}
    </div>
  </div>
);

// ─── Read-only record rows ────────────────────────────────────────────────────
const RecordRow = ({ label, value, sub }) => (
  <div style={{ display: 'flex', gap: 10, padding: '6px 0', borderBottom: '1px solid #f3f4f6', flexWrap: 'wrap' }}>
    <span style={{ fontSize: 12, color: '#888', minWidth: 80 }}>{label}</span>
    <span style={{ fontSize: 13, color: '#333', flex: 1 }}>
      {value}
      {sub && <span style={{ fontSize: 11, color: '#777', display: 'block' }}>{sub}</span>}
    </span>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const DRPInchargeDashboard = () => {
  const { logout } = useAuth();
  const [user, setUser] = useState(null);
  const [students, setStudents] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [toast, setToast] = useState('');

  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) setUser(JSON.parse(userData));
  }, []);

  const loadStudents = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/classic-drp/incharge/my-students`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (d.success) setStudents(d.entries);
    } catch { /* silent */ }
    setLoading(false);
  }, [token]);

  useEffect(() => { loadStudents(); }, [loadStudents]);

  const loadStudentEntry = async (studentId) => {
    try {
      const r = await fetch(`${API}/classic-drp/incharge/student/${studentId}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (d.success) {
        setSelectedEntry(d.entry);
        setRemarks(d.entry.inchargeVerification?.remarks || '');
      } else showToast(d.message);
    } catch { showToast('Failed to load student data'); }
  };

  const updateVerification = async (updates) => {
    if (!selectedEntry) return;
    setSaving(true);
    try {
      const currentV = selectedEntry.inchargeVerification || {};
      const body = { ...currentV, ...updates, remarks };
      const r = await fetch(`${API}/classic-drp/incharge/verify/${selectedEntry.student._id}`, {
        method: 'PUT', headers, body: JSON.stringify(body)
      });
      const d = await r.json();
      if (d.success) {
        setSelectedEntry(prev => ({ ...prev, inchargeVerification: d.inchargeVerification }));
        showToast('Verification updated');
      } else showToast(d.message);
    } catch { showToast('Failed to update'); }
    setSaving(false);
  };

  const saveRemarks = async () => {
    setSaving(true);
    const v = selectedEntry?.inchargeVerification || {};
    await updateVerification({ remarks });
    setSaving(false);
  };

  const issueCertificate = async () => {
    if (!selectedEntry) return;
    if (!window.confirm('Issue certificate for this student? They will be able to download it.')) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/classic-drp/incharge/issue-certificate/${selectedEntry.student._id}`, {
        method: 'POST', headers
      });
      const d = await r.json();
      if (d.success) {
        showToast('Certificate issued! Generating PDF...');
        setSelectedEntry(prev => ({ ...prev, certificate: d.certificate }));
        // Also generate PDF for incharge
        await downloadCert(selectedEntry);
        loadStudents();
      } else showToast(d.message);
    } catch { showToast('Failed to issue certificate'); }
    setSaving(false);
  };

  const downloadCert = async (entry) => {
    setDownloading(true);
    const e = entry || selectedEntry;
    const inchargeName = user ? `${user.profile?.firstName || ''} ${user.profile?.lastName || ''}`.trim() : '';
    const courseName = e?.student?.assignedCourses?.[0]?.courseName || '';
    await generateClassicDRPCertificate({
      studentName: `${e?.student?.profile?.firstName || ''} ${e?.student?.profile?.lastName || ''}`.trim(),
      courseName,
      hospitalName: e?.profile?.hospitalName,
      fromDate: e?.profile?.fromDate,
      toDate: e?.profile?.toDate,
      inchargeName
    }, collegeConfig.collegeName, collegeConfig.logo);
    setDownloading(false);
  };

  if (loading) return (
    <div style={styles.centered}>
      <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: 32, color: '#b11217' }} />
    </div>
  );

  const v = selectedEntry?.inchargeVerification || {};
  const allVerified = v.attendanceVerified && v.diaryVerified && v.operationsVerified && v.publicHealthVerified;

  if (selectedEntry) {
    const s = selectedEntry.student;
    const studentName = `${s?.profile?.firstName || ''} ${s?.profile?.lastName || ''}`.trim();
    const courseName = s?.assignedCourses?.[0]?.courseName || '';

    return (
      <div style={styles.container}>
        {toast && <div style={styles.toast}>{toast}</div>}
        <div style={styles.header}>
          <button onClick={() => setSelectedEntry(null)} style={styles.backBtn}><FaArrowLeft /></button>
          <div>
            <h1 style={styles.headerTitle}>{studentName}</h1>
            <span style={styles.headerSub}>{courseName} · {s?.profile?.registrationNumber || ''}</span>
          </div>
        </div>

        <div style={styles.infoCard}>
          <div style={styles.detailGrid}>
            <div><span style={styles.detailLabel}>Hospital</span><span style={styles.detailValue}>{selectedEntry.profile?.hospitalName || '—'}</span></div>
            <div><span style={styles.detailLabel}>Duration</span><span style={styles.detailValue}>{formatDate(selectedEntry.profile?.fromDate)} – {formatDate(selectedEntry.profile?.toDate)}</span></div>
            <div><span style={styles.detailLabel}>Attendance</span><span style={styles.detailValue}>{selectedEntry.attendance?.length || 0} days</span></div>
            <div><span style={styles.detailLabel}>Diary</span><span style={styles.detailValue}>{selectedEntry.diary?.length || 0} entries</span></div>
          </div>
        </div>

        {/* Remarks */}
        <div style={styles.card}>
          <label style={styles.label}>Your Remarks for Student</label>
          <textarea
            rows={3}
            value={remarks}
            onChange={e => setRemarks(e.target.value)}
            placeholder="Write your remarks or feedback for the student..."
            style={{ ...styles.input, resize: 'vertical' }}
          />
          <button onClick={saveRemarks} disabled={saving} style={styles.outlineBtn}>
            {saving ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> : <><FaSave style={{ marginRight: 6 }} />Save Remarks</>}
          </button>
        </div>

        {/* Daily Attendance */}
        <VerifySection
          label="Daily Attendance"
          verified={v.attendanceVerified}
          count={selectedEntry.attendance?.length || 0}
          onToggle={() => updateVerification({ attendanceVerified: !v.attendanceVerified })}
        >
          {selectedEntry.attendance?.length === 0 ? <p style={styles.empty}>No attendance records</p> : (
            selectedEntry.attendance.map(a => (
              <RecordRow key={a._id} label={formatDate(a.date)} value={a.posting} />
            ))
          )}
        </VerifySection>

        {/* Daily Diary */}
        <VerifySection
          label="Daily Diary"
          verified={v.diaryVerified}
          count={selectedEntry.diary?.length || 0}
          onToggle={() => updateVerification({ diaryVerified: !v.diaryVerified })}
        >
          {selectedEntry.diary?.length === 0 ? <p style={styles.empty}>No diary entries</p> : (
            selectedEntry.diary.map(d => (
              <RecordRow key={d._id} label={`${formatDate(d.date)} (${d.day || ''})`} value={d.entry} />
            ))
          )}
        </VerifySection>

        {/* Operations */}
        <VerifySection
          label="Record of Operations"
          verified={v.operationsVerified}
          count={selectedEntry.operations?.length || 0}
          onToggle={() => updateVerification({ operationsVerified: !v.operationsVerified })}
        >
          {selectedEntry.operations?.length === 0 ? <p style={styles.empty}>No operation records</p> : (
            selectedEntry.operations.map(op => (
              <RecordRow
                key={op._id}
                label={formatDate(op.date)}
                value={`${op.patientName}${op.ipNumber ? ` · IP: ${op.ipNumber}` : ''}`}
                sub={op.category}
              />
            ))
          )}
        </VerifySection>

        {/* Public Health */}
        <VerifySection
          label="Public Health Activities"
          verified={v.publicHealthVerified}
          count={selectedEntry.publicHealth?.length || 0}
          onToggle={() => updateVerification({ publicHealthVerified: !v.publicHealthVerified })}
        >
          {selectedEntry.publicHealth?.length === 0 ? <p style={styles.empty}>No public health activities</p> : (
            selectedEntry.publicHealth.map(ph => (
              <RecordRow key={ph._id} label={formatDate(ph.date)} value={ph.activity} />
            ))
          )}
        </VerifySection>

        {/* Certificate */}
        <div style={{ ...styles.card, textAlign: 'center' }}>
          {selectedEntry.certificate?.issued ? (
            <>
              <FaAward style={{ fontSize: 36, color: '#d97706', marginBottom: 8 }} />
              <p style={{ fontWeight: 600, color: '#1a1a2e' }}>Certificate already issued on {formatDate(selectedEntry.certificate.issuedAt)}</p>
              <button onClick={() => downloadCert()} disabled={downloading} style={{ ...styles.primaryBtn, margin: '8px auto 0', display: 'inline-flex' }}>
                {downloading ? <FaSpinner style={{ animation: 'spin 1s linear infinite', marginRight: 6 }} /> : <FaDownload style={{ marginRight: 8 }} />}
                Download Certificate
              </button>
            </>
          ) : (
            <>
              <div style={{ ...styles.infoBanner, marginBottom: 12, textAlign: 'left' }}>
                <FaInfoCircle style={{ color: '#2563eb', flexShrink: 0 }} />
                <span style={{ fontSize: 13 }}>All 4 sections must be verified before issuing the certificate.</span>
              </div>
              <button
                onClick={issueCertificate}
                disabled={!allVerified || saving}
                style={{
                  ...styles.primaryBtn,
                  display: 'inline-flex',
                  margin: '0 auto',
                  opacity: allVerified ? 1 : 0.5,
                  cursor: allVerified ? 'pointer' : 'not-allowed'
                }}
              >
                {saving ? <FaSpinner style={{ animation: 'spin 1s linear infinite', marginRight: 6 }} /> : <FaAward style={{ marginRight: 8 }} />}
                Issue Certificate
              </button>
              {!allVerified && (
                <p style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
                  Still pending: {[
                    !v.attendanceVerified && 'Attendance',
                    !v.diaryVerified && 'Daily Diary',
                    !v.operationsVerified && 'Operations',
                    !v.publicHealthVerified && 'Public Health'
                  ].filter(Boolean).join(', ')}
                </p>
              )}
            </>
          )}
        </div>

        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ── Student List View ─────────────────────────────────────────────────────
  const inchargeName = user ? `${user.profile?.firstName || ''} ${user.profile?.lastName || ''}`.trim() : '';
  const hospitalName = user?.drpInchargeDetails?.hospitalName || '';

  return (
    <div style={styles.container}>
      {toast && <div style={styles.toast}>{toast}</div>}
      <div style={styles.header}>
        <FaUserMd style={{ fontSize: 22, color: '#b11217' }} />
        <div style={{ flex: 1 }}>
          <h1 style={styles.headerTitle}>DRP Incharge Dashboard</h1>
          {hospitalName && <span style={styles.headerSub}>{hospitalName}</span>}
        </div>
        <button onClick={logout} style={styles.logoutBtn} title="Logout">
          <FaSignOutAlt />
        </button>
      </div>

      <div style={styles.welcomeCard}>
        <FaUserMd style={{ fontSize: 32, color: '#b11217', marginBottom: 8 }} />
        <h2 style={{ margin: 0, fontSize: 18, color: '#1a1a2e' }}>Welcome, {inchargeName}</h2>
        {hospitalName && <p style={{ margin: '4px 0 0', color: '#666', fontSize: 13 }}>{hospitalName}</p>}
        <div style={styles.statsRow}>
          <div style={styles.statItem}>
            <span style={styles.statNum}>{students.length}</span>
            <span style={styles.statLabel}>Students</span>
          </div>
          <div style={styles.statItem}>
            <span style={styles.statNum}>
              {students.filter(s => s.certificate?.issued).length}
            </span>
            <span style={styles.statLabel}>Certified</span>
          </div>
          <div style={styles.statItem}>
            <span style={styles.statNum}>
              {students.filter(s => {
                const v = s.inchargeVerification || {};
                return v.attendanceVerified && v.diaryVerified && v.operationsVerified && v.publicHealthVerified && !s.certificate?.issued;
              }).length}
            </span>
            <span style={styles.statLabel}>Pending Cert</span>
          </div>
        </div>
      </div>

      <div style={{ padding: '0 12px 12px' }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e', marginBottom: 12 }}>My Students</h2>
        {students.length === 0 ? (
          <div style={styles.emptyState}>
            <FaUsers style={{ fontSize: 40, color: '#ccc', marginBottom: 12 }} />
            <p style={{ color: '#999', fontSize: 14 }}>No students have selected you as their DRP Incharge yet.</p>
            <p style={{ color: '#bbb', fontSize: 12 }}>Students will select you when setting up their DRP profile.</p>
          </div>
        ) : (
          students.map(entry => {
            const s = entry.student;
            const vv = entry.inchargeVerification || {};
            const allV = vv.attendanceVerified && vv.diaryVerified && vv.operationsVerified && vv.publicHealthVerified;
            const certified = entry.certificate?.issued;
            const studentName = `${s?.profile?.firstName || ''} ${s?.profile?.lastName || ''}`.trim();
            const courseName = s?.assignedCourses?.[0]?.courseName || '';
            return (
              <div key={entry._id} style={styles.studentCard} onClick={() => loadStudentEntry(s._id)}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <div style={styles.avatar}>{studentName.charAt(0).toUpperCase()}</div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#1a1a2e' }}>{studentName}</h3>
                    <p style={{ margin: '2px 0', fontSize: 12, color: '#666' }}>{courseName} · {s?.profile?.registrationNumber || ''}</p>
                    {entry.profile?.hospitalName && (
                      <p style={{ margin: '2px 0', fontSize: 12, color: '#888' }}>
                        <FaHospital size={10} style={{ marginRight: 4 }} />{entry.profile.hospitalName}
                      </p>
                    )}
                    <p style={{ margin: '2px 0', fontSize: 12, color: '#888' }}>
                      <FaCalendarAlt size={10} style={{ marginRight: 4 }} />
                      {formatDate(entry.profile?.fromDate)} – {formatDate(entry.profile?.toDate)}
                    </p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    {certified ? (
                      <span style={styles.badgeCert}><FaAward size={10} style={{ marginRight: 4 }} />Certified</span>
                    ) : allV ? (
                      <span style={styles.badgeReady}>Ready for Cert</span>
                    ) : (
                      <span style={styles.badgePending}>In Progress</span>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                  {[
                    { key: 'attendanceVerified', label: 'Attendance' },
                    { key: 'diaryVerified', label: 'Diary' },
                    { key: 'operationsVerified', label: 'Operations' },
                    { key: 'publicHealthVerified', label: 'Public Health' }
                  ].map(item => (
                    <span key={item.key} style={{
                      fontSize: 11, padding: '2px 8px', borderRadius: 10,
                      background: vv[item.key] ? '#d1fae5' : '#f3f4f6',
                      color: vv[item.key] ? '#065f46' : '#666',
                      fontWeight: 500
                    }}>
                      {vv[item.key] ? '✓' : '○'} {item.label}
                    </span>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

const styles = {
  container: { maxWidth: 680, margin: '0 auto', padding: '0 0 32px', fontFamily: 'sans-serif', background: '#f5f5f7', minHeight: '100vh' },
  centered: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' },
  header: { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.1)', position: 'sticky', top: 0, zIndex: 10 },
  headerTitle: { margin: 0, fontSize: 17, fontWeight: 700, color: '#1a1a2e' },
  headerSub: { fontSize: 12, color: '#888' },
  backBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#b11217', padding: 4 },
  logoutBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#666', padding: 4 },
  welcomeCard: { margin: 12, padding: '20px 16px', background: '#fff', borderRadius: 14, textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  statsRow: { display: 'flex', justifyContent: 'center', gap: 24, marginTop: 16 },
  statItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 },
  statNum: { fontSize: 24, fontWeight: 800, color: '#b11217' },
  statLabel: { fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' },
  infoCard: { margin: '0 12px 12px', padding: 14, background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  card: { margin: '0 12px 12px', padding: 14, background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  detailGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  detailLabel: { display: 'block', fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 },
  detailValue: { display: 'block', fontSize: 14, color: '#1a1a2e', fontWeight: 500 },
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 },
  input: { width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none' },
  primaryBtn: { background: '#b11217', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', fontSize: 14 },
  outlineBtn: { background: 'none', color: '#b11217', border: '1px solid #b11217', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginTop: 8 },
  empty: { color: '#999', fontSize: 13, fontStyle: 'italic', margin: '4px 0' },
  emptyState: { textAlign: 'center', padding: '40px 20px' },
  studentCard: { background: '#fff', borderRadius: 12, padding: 14, marginBottom: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', cursor: 'pointer', border: '1px solid #e5e7eb' },
  avatar: { width: 44, height: 44, borderRadius: '50%', background: '#b11217', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700, flexShrink: 0 },
  badgeCert: { fontSize: 11, padding: '3px 8px', borderRadius: 10, background: '#d97706', color: '#fff', fontWeight: 600, display: 'inline-flex', alignItems: 'center' },
  badgeReady: { fontSize: 11, padding: '3px 8px', borderRadius: 10, background: '#059669', color: '#fff', fontWeight: 600 },
  badgePending: { fontSize: 11, padding: '3px 8px', borderRadius: 10, background: '#f3f4f6', color: '#666', fontWeight: 500 },
  infoBanner: { display: 'flex', gap: 8, padding: 10, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, alignItems: 'flex-start' },
  toast: { position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', background: '#1a1a2e', color: '#fff', padding: '10px 20px', borderRadius: 20, fontSize: 14, zIndex: 2000, boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }
};

export default DRPInchargeDashboard;
